/**
 * Public site settings provider – loads public settings and keeps tabs in sync.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  SITE_SETTINGS_BROADCAST_CHANNEL,
  SITE_SETTINGS_CHANGED_EVENT,
  SITE_SETTINGS_POLL_INTERVAL_PUBLIC_MS,
  SITE_SETTINGS_POLL_INTERVAL_SIGNED_IN_MS,
} from '@constants/siteSettingsSync';
import { useAuth } from '@contexts/AuthContext';
import type { SiteSettings } from '@lambda/shared/types';
import { siteSettingsApi } from '@services/siteSettingsApi';
import type { SiteSettingsBroadcastMessage } from '@utils/siteSettingsSync';
import {
  isNewRegistrationAllowed,
  registrationClosedNoticeText,
  shouldShowRegistrationClosedNotice,
} from '@utils/registrationSettings';
import { resolveDirectorEmail } from '@utils/directorEmail';
import {
  readPublicSiteSettingsCache,
  writePublicSiteSettingsCache,
} from '@utils/publicSiteSettingsCache';
import { PublicSiteSettingsContext, type PublicSiteSettingsValue } from './PublicSiteSettingsContext';

/** Stop blocking registration UI if site-settings is slow or unavailable (landing still renders). */
const SITE_SETTINGS_LOADING_CAP_MS = 4_000;

export function PublicSiteSettingsProvider({ children }: { children: ReactNode }) {
  const { token, user } = useAuth();
  const isSignedIn = Boolean(token && user);
  const pollIntervalMs = isSignedIn
    ? SITE_SETTINGS_POLL_INTERVAL_SIGNED_IN_MS
    : SITE_SETTINGS_POLL_INTERVAL_PUBLIC_MS;

  const cachedOnMount = readPublicSiteSettingsCache();
  const [settings, setSettings] = useState<SiteSettings | null>(cachedOnMount);
  const [loading, setLoading] = useState(cachedOnMount === null);

  /** Fetches settings and applies them when the response arrives; never rejects. */
  const fetchSettings = useCallback(
    (): Promise<void> =>
      siteSettingsApi.get().then(
        (res) => {
          const next = res.data?.settings ?? null;
          setSettings(next);
          if (next) writePublicSiteSettingsCache(next);
        },
        () => {
          setSettings(null);
        }
      ),
    []
  );

  const refetch = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    let cancelled = false;
    void fetchSettings().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchSettings]);

  useEffect(() => {
    if (!loading) return;
    const capId = window.setTimeout(() => {
      setLoading(false);
    }, SITE_SETTINGS_LOADING_CAP_MS);
    return () => window.clearTimeout(capId);
  }, [loading]);

  useEffect(() => {
    if (loading) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void fetchSettings();
    }, pollIntervalMs);
    return () => window.clearInterval(intervalId);
  }, [loading, fetchSettings, pollIntervalMs]);

  useEffect(() => {
    if (loading) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void fetchSettings();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [loading, fetchSettings]);

  useEffect(() => {
    const onChanged = () => {
      const cached = readPublicSiteSettingsCache();
      if (cached) setSettings(cached);
      void fetchSettings();
    };
    window.addEventListener(SITE_SETTINGS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SITE_SETTINGS_CHANGED_EVENT, onChanged);
  }, [fetchSettings]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(SITE_SETTINGS_BROADCAST_CHANNEL);
    channel.onmessage = (event: MessageEvent<SiteSettingsBroadcastMessage>) => {
      if (event.data?.type === 'site-settings-changed') {
        void fetchSettings();
      }
    };
    return () => channel.close();
  }, [fetchSettings]);

  const value = useMemo((): PublicSiteSettingsValue => {
    const registrationPending = loading && settings === null;
    return {
      settings,
      loading,
      registrationPending,
      registrationsOpen: settings != null && isNewRegistrationAllowed(settings),
      showRegistrationClosedNotice: shouldShowRegistrationClosedNotice(settings),
      registrationClosedNoticeText: registrationClosedNoticeText(settings),
      directorEmail: resolveDirectorEmail(settings),
      refetch,
    };
  }, [settings, loading, refetch]);

  return (
    <PublicSiteSettingsContext value={value}>
      {children}
    </PublicSiteSettingsContext>
  );
}
